import React, { useState, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Trash2, Plus, Search } from 'lucide-react';
import { accessorialCodeAPI } from '../api';
import { toast } from 'sonner';

// Settings tab: accessorial code catalog (list, search, add, delete)
export const AccessorialCodesTab = () => {
  const [codes, setCodes] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: '', typical_use: '' });

  const fetchCodes = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const res = await accessorialCodeAPI.search(q);
      setCodes(res.data || []);
    } catch {
      toast.error('Failed to load accessorial codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  useEffect(() => {
    const t = setTimeout(() => fetchCodes(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query, fetchCodes]);

  const handleAdd = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      const res = await accessorialCodeAPI.create({ name: form.name.trim(), typical_use: form.typical_use.trim() });
      toast.success(`Code ${res.data.code} created`);
      setAddOpen(false);
      setForm({ name: '', typical_use: '' });
      fetchCodes(query.trim());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create code');
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete code ${code.code} (${code.name})?`)) return;
    try {
      await accessorialCodeAPI.delete(code.id);
      toast.success('Code deleted');
      fetchCodes(query.trim());
    } catch {
      toast.error('Failed to delete code');
    }
  };

  return (
    <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-4 md:p-6" data-testid="accessorial-codes-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-[var(--tms-text)]">Accessorial Codes</h2>
          <p className="text-sm text-[var(--tms-text-secondary)]">{codes.length} codes in catalog</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[var(--tms-text-muted)]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search codes..."
              className="pl-8 h-9 w-56"
              data-testid="acc-codes-search"
            />
          </div>
          <Button onClick={() => setAddOpen(true)} className="h-9" data-testid="acc-codes-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Code
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--tms-border)] text-left text-xs uppercase text-[var(--tms-text-muted)]">
              <th className="py-2 px-3 w-20">Code</th>
              <th className="py-2 px-3">Accessorial</th>
              <th className="py-2 px-3">Typical use</th>
              <th className="py-2 px-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="py-6 text-center text-[var(--tms-text-muted)]">Loading...</td></tr>
            ) : codes.length === 0 ? (
              <tr><td colSpan={4} className="py-6 text-center text-[var(--tms-text-muted)]">No codes found</td></tr>
            ) : codes.map(code => (
              <tr key={code.id} className="border-b border-[var(--tms-border)] hover:bg-[var(--tms-hover)]" data-testid={`acc-code-row-${code.code}`}>
                <td className="py-2 px-3 font-mono font-bold text-[var(--tms-primary)]">{code.code}</td>
                <td className="py-2 px-3 font-medium text-[var(--tms-text)]">{code.name}</td>
                <td className="py-2 px-3 text-[var(--tms-text-secondary)]">{code.typical_use}</td>
                <td className="py-2 px-3">
                  <button
                    onClick={() => handleDelete(code)}
                    className="p-1 text-[var(--tms-text-muted)] hover:text-[var(--tms-error)]"
                    title="Delete code"
                    data-testid={`acc-code-delete-${code.code}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm" data-testid="acc-codes-add-modal">
          <DialogHeader>
            <DialogTitle>New Accessorial Code</DialogTitle>
            <DialogDescription>A unique 4-letter code will be generated from the name</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-[var(--tms-text)]">Accessorial</label>
              <Input
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Name of the accessorial"
                className="mt-1"
                data-testid="acc-codes-new-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[var(--tms-text)]">Typical use</label>
              <Input
                value={form.typical_use}
                onChange={(e) => setForm(prev => ({ ...prev, typical_use: e.target.value }))}
                placeholder="Brief description"
                className="mt-1"
                data-testid="acc-codes-new-use"
              />
            </div>
            <Button onClick={handleAdd} className="w-full" data-testid="acc-codes-new-save">Create Code</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
